import type { FilaCartera } from '@/lib/supabase/tipos-bd'

import { EDADES, edadDe, estadoDe, type ClaveEdad } from './edades'

/**
 * CÓMO SE AGRUPAN LAS TARJETAS DE LA CARTERA.
 *
 * Johan: «que me aparezca por edades y que yo pueda agrupar por cliente, o
 * por ODT, etc.», y después: «en la misma cartera, que se viera por cards».
 * Cada tarjeta ya es una orden; agrupar es ponerlas en secciones con lo que
 * debe cada sección y cómo se reparte por edad.
 */
export type Agrupar = 'edad' | 'cliente' | 'orden' | 'mes' | 'servicio'

export const AGRUPACIONES: [Agrupar, string][] = [
  ['edad', 'Edad'],
  ['cliente', 'Cliente'],
  ['orden', 'Orden (sin agrupar)'],
  ['mes', 'Mes en que se finalizó'],
  ['servicio', 'Servicio'],
]

export type GrupoDeCartera = {
  clave: string
  titulo: string
  /** La edad, cuando se agrupa por edad: pinta la cabecera de su color. */
  edad: ClaveEdad | null
  filas: FilaCartera[]
  saldo: number
  /** Lo que se debe repartido por edad, para la barrita de la cabecera. */
  porEdad: Record<ClaveEdad, number>
}

const MESES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
]

const mesDe = (iso: string) => {
  const d = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Bogota', year: 'numeric', month: '2-digit' })
    .format(new Date(iso))
  const [a, m] = d.split('-')
  return { clave: `${a}-${m}`, titulo: `${MESES[Number(m) - 1]} de ${a}` }
}

/** Una cuenta que ya no debe no tiene edad de cobro: va en su propia sección. */
function llaveDeEdad(c: FilaCartera): { clave: string; titulo: string; edad: ClaveEdad | null } {
  if (Number(c.saldo) > 0) {
    const e = edadDe(c.dias)
    return { clave: `0${EDADES.indexOf(e)}`, titulo: e.rotulo, edad: e.clave }
  }
  return estadoDe(c) === 'pagada'
    ? { clave: '8', titulo: 'Pagadas', edad: null }
    : { clave: '9', titulo: 'Sin documentos', edad: null }
}

function llaveDe(c: FilaCartera, por: Agrupar) {
  switch (por) {
    case 'edad':
      return llaveDeEdad(c)
    case 'cliente':
      return { clave: c.cliente ?? 'Sin cliente', titulo: c.cliente ?? 'Sin cliente', edad: null }
    case 'mes':
      return { ...mesDe(c.finalizado_en), edad: null }
    case 'servicio':
      return { clave: c.servicio ?? 'Sin servicio', titulo: c.servicio ?? 'Sin servicio', edad: null }
    case 'orden':
      return { clave: '', titulo: '', edad: null }
  }
}

/** null: sin agrupar (cada tarjeta es una orden). */
export function agruparCuentas(filas: FilaCartera[], por: Agrupar): GrupoDeCartera[] | null {
  if (por === 'orden') return null
  const mapa = new Map<string, GrupoDeCartera>()
  for (const c of filas) {
    const llave = llaveDe(c, por)
    let g = mapa.get(llave.clave)
    if (!g) {
      g = { ...llave, filas: [], saldo: 0, porEdad: { e0: 0, e1: 0, e2: 0, e3: 0 } }
      mapa.set(llave.clave, g)
    }
    g.filas.push(c)
    const saldo = Number(c.saldo)
    if (saldo > 0) {
      g.saldo += saldo
      g.porEdad[edadDe(c.dias).clave] += saldo
    }
  }
  const grupos = [...mapa.values()]
  // Edad: de la más nueva a la más vieja. Mes: el más reciente arriba.
  // Lo demás: quien más debe, arriba.
  if (por === 'edad') return grupos.sort((a, b) => a.clave.localeCompare(b.clave))
  if (por === 'mes') return grupos.sort((a, b) => b.clave.localeCompare(a.clave))
  return grupos.sort((a, b) => b.saldo - a.saldo || a.titulo.localeCompare(b.titulo))
}
